/****
 * Timezone Bar component that displays real-time clocks for user-selected
 * locations, highlights office-hour availability across regions, and allows
 * users to customize the order of displayed timezones through drag-and-drop
 * configuration.
 */
import { DotsHorizontal, Menu02, Settings01 } from "@untitledui/icons";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useDeviceType } from "@/hooks/useDeviceType";
import { getUserTimezones, saveUserTimezones } from "@/services/timezones";
import type { Timezone } from "@/shared/types/Common";

const TimezoneBar = ({ upn }: { upn: string }): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const [cities, setCities] = useState<Timezone[]>([]);

  const [hiddenOpen, setHiddenOpen] = useState(false);
  const hiddenRef = useRef<HTMLDivElement | null>(null);

  const [times24, setTimes24] = useState<Record<string, string>>({});
  const [times12, setTimes12] = useState<Record<string, string>>({});
  const [dates, setDates] = useState<Record<string, string>>({});
  const [officeHours, setOfficeHours] = useState<Record<string, boolean>>({});

  const [manageOpen, setManageOpen] = useState(false);
  const [localCities, setLocalCities] = useState<Timezone[]>([]);
  const { isMobile } = useDeviceType();

  const [savedCities, setSavedCities] = useState<Timezone[] | null>(null);
  const [isTouchDragging, setIsTouchDragging] = useState(false);

  const [visibleCount, setVisibleCount] = useState(5);

  // Load user-configured timezones when the component initializes
  useEffect(() => {
    const loadTimezones = async () => {
      try {
        const data = await getUserTimezones(upn);

        setCities(data);
        setSavedCities(data);
      } catch (err) {
        console.error("Failed to load timezones:", err);
      }
    };

    if (upn) {
      loadTimezones();
    }
  }, [upn]);

  // Sync edit state
  useEffect(() => {
    if (!manageOpen) setLocalCities(cities);
  }, [cities, manageOpen]);

  const activeCities = manageOpen ? localCities : (savedCities ?? cities);

  const visibleCities = useMemo(
    () => activeCities.slice(0, visibleCount),
    [activeCities, visibleCount]
  );

  const hiddenCities = useMemo(
    () => activeCities.slice(visibleCount),
    [activeCities, visibleCount]
  );

  // Formatters (stable)
  const formatters = useMemo(() => {
    const locale = i18n.language || "en";

    return cities.reduce<Record<string, any>>((acc, c) => {
      acc[c.key] = {
        time24: new Intl.DateTimeFormat(locale, {
          timeZone: c.timezone,
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }),
        time12: new Intl.DateTimeFormat(locale, {
          timeZone: c.timezone,
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
        hour: new Intl.DateTimeFormat("en-US", {
          timeZone: c.timezone,
          hour: "numeric",
          hour12: false,
        }),
        date: new Intl.DateTimeFormat(locale, {
          timeZone: c.timezone,
          weekday: "long",
          month: "short",
          day: "2-digit",
          year: "numeric",
        }),
      };
      return acc;
    }, {});
  }, [cities, i18n.language]);

  useEffect(() => {
    const updateVisibleCount = () => {
      const width = window.innerWidth;

      if (width >= 1280) {
        setVisibleCount(5); // large desktop
      } else if (width >= 1024) {
        setVisibleCount(4); // laptop
      } else if (width >= 768) {
        setVisibleCount(3); // tablet
      } else if (width >= 670) {
        setVisibleCount(2); // small tablet
      } else {
        setVisibleCount(1); // very small (pre-mobile)
      }
    };

    updateVisibleCount();
    window.addEventListener("resize", updateVisibleCount);

    return () => window.removeEventListener("resize", updateVisibleCount);
  }, []);

  // TIME + OFFICE HOURS (interval only + logger )
  useEffect(() => {
    const update = () => {
      const now = new Date();

      const nextTimes24: Record<string, string> = {};
      const nextTimes12: Record<string, string> = {};
      const nextOffice: Record<string, boolean> = {};
      const nextDates: Record<string, string> = {};

      cities.forEach((city) => {
        const f = formatters[city.key];
        if (!f) return;

        const localHour = Number(f.hour.format(now));

        const isOffice = localHour >= city.OfficeStartHour && localHour < city.OfficeEndHour;

        nextTimes24[city.key] = f.time24.format(now);
        nextTimes12[city.key] = f.time12.format(now);
        nextDates[city.key] = f.date.format(now);
        nextOffice[city.key] = isOffice;
      });

      setTimes24(nextTimes24);
      setTimes12(nextTimes12);
      setDates(nextDates);
      setOfficeHours(nextOffice);
    };

    update(); // initial
    const id = window.setInterval(update, 1000);
    return () => clearInterval(id);
  }, [formatters, i18n.language]); // NO cities dependency

  // Drag
  const onDragStart = (index: number) => (e: React.DragEvent) => {
    e.dataTransfer.setData("index", String(index));
  };

  const onDrop = (index: number) => (e: React.DragEvent) => {
    const from = Number(e.dataTransfer.getData("index"));
    const updated = [...localCities];
    const [moved] = updated.splice(from, 1);
    updated.splice(index, 0, moved);
    setLocalCities(updated);
  };

  const onDragOver = (e: React.DragEvent) => e.preventDefault();

  const handleSave = async () => {
    try {
      const payload = {
        upn,
        timezones: localCities.map((c, i) => ({
          navbar_timezone_id: c.id || c.navbar_timezone_id,
          display_order: i + 1,
        })),
      };

      await saveUserTimezones(payload);

      const updated = await getUserTimezones(upn);

      // Merge office hours from existing cities
      const enriched = updated.map((u: any) => {
        const existing = cities.find((c) => c.key === u.key || c.timezone === u.timezone);

        return {
          ...u,
          OfficeStartHour: existing?.OfficeStartHour,
          OfficeEndHour: existing?.OfficeEndHour,
        };
      });

      setCities(enriched);

      setSavedCities(enriched);
      setLocalCities(enriched);

      setManageOpen(false);
      setHiddenOpen(false);
    } catch (err) {
      console.error("Failed to save timezone order:", err);
    }
  };

  // Outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!hiddenRef.current) return;

      if (!hiddenRef.current.contains(e.target as Node) && !isTouchDragging) {
        setHiddenOpen(false);
        setManageOpen(false);
        setLocalCities(cities);
      }
    };

    if (hiddenOpen) {
      document.addEventListener("mousedown", handleClick);
    }

    return () => document.removeEventListener("mousedown", handleClick);
  }, [hiddenOpen, cities, isTouchDragging]);

  useEffect(() => {
    if (hiddenOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [hiddenOpen]);

  // Navbar city render
  const renderCity = (city: Timezone) => {
    const label = city.label;
    return (
      <div
        key={city.key}
        className="relative group z-50 text-center"
        tabIndex={0}
      >
        <dl>
          <dt className="block text-[10px] font-semibold opacity-60">
            {label.toLocaleUpperCase()}
          </dt>

          <dd className="block text-[14px] font-semibold mt-0.5">
            {times24[city.key]}
          </dd>
        </dl>

        <div className="pointer-events-none absolute left-1/2 top-full hidden -translate-x-1/2 pt-3 group-hover:block group-focus-within:block">          <div className="w-56 rounded-xl border p-4 text-center shadow-xl backdrop-blur bg-[var(--panel-bg)] text-[var(--panel-text)] border-gray-300">
          <p className="text-xs font-semibold opacity-60">
            {city.label}, {city.country}
          </p>

          <p className="mt-2 text-3xl font-bold">{times12[city.key]}</p>

          <p className="text-xs mt-1 opacity-70">{dates[city.key]}</p>

          <div className="mt-4 flex items-center justify-center gap-2 text-sm">
            <span
              className={`h-2 w-2 rounded-full ${officeHours[city.key] ? "bg-[var(--color-brand)]" : "bg-gray-500"
                }`}
            />
            <span>
              {officeHours[city.key]
                ? t("timezoneBar.officeHoursNow", {
                  defaultValue: "Office hours now",
                })
                : t("timezoneBar.officeHoursClosed", {
                  defaultValue: "Outside office hours",
                })}
            </span>
          </div>
        </div>
        </div>
      </div>
    );
  };

  return (
    <div className="timezone-bar">
      {/*  MOBILE VIEW */}
      {isMobile ? (
        <div className="p-4 space-y-4">
          {cities.map((city) => (
            <div
              key={city.key}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-sm font-medium uppercase">{city.label}</h2>
                  <p className="text-sm text-gray-500">{city.country}</p>
                </div>

                <div
                  className={`flex items-center gap-2 text-xs px-3 py-1 rounded-full border ${officeHours[city.key]
                    ? "border-[var(--color-brand)] text-[var(--color-brand)]"
                    : "border-gray-400 text-gray-500"
                    }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${officeHours[city.key] ? "bg-[var(--color-brand)]" : "bg-gray-500"
                      }`}
                  />
                  <span>
                    {officeHours[city.key]
                      ? t("timezoneBar.officeHoursNow", {
                        defaultValue: "Office Hours",
                      })
                      : t("timezoneBar.officeHoursClosed", {
                        defaultValue: "Outside Office Hours",
                      })}
                  </span>
                </div>
              </div>

              <div className="mt-4 text-4xl font-bold">{times24[city.key]}</div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {/*  DESKTOP VIEW (UNCHANGED) */}
          <div className="flex items-center gap-6">
            {visibleCities.map(renderCity)}

            <div className="relative">
              {hiddenCities.length > 0 && (
                <button
                  type="button"
                  aria-label={t("timezoneBar.moreLocationsOptions")}
                  aria-expanded={hiddenOpen}
                  aria-haspopup="menu"
                  aria-controls="timezone-menu"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => setHiddenOpen((v) => !v)}
                  className="p-1 rounded-md"
                >
                  <DotsHorizontal className="w-5 h-5" aria-hidden="true" />
                </button>
              )}
              <div
                ref={hiddenRef}
                className={`absolute right-0 top-full mt-2 ${hiddenOpen ? "block" : "hidden"}`}
              >
                <div
                  id="timezone-menu"
                  role="menu"
                  className="timezone-popup w-60 rounded-lg border bg-[var(--panel-bg)] text-[var(--panel-text)] border-[var(--panel-sep)] shadow-lg"
                >                  {!manageOpen ? (
                  <>
                    {/*  Hidden cities */}
                    {hiddenCities.map((city) => (
                      <div
                        key={city.key}
                        className="relative group"
                        tabIndex={0}
                      >
                        <dl className="flex justify-between px-3 py-2 text-sm hover:bg-gray-100">
                          <dt className="text-xs">
                            {city.label.toLocaleUpperCase()}
                          </dt>

                          <dd>
                            {times24[city.key]}
                          </dd>
                        </dl>
                        {/*  Tooltip */}
                        <div className="pointer-events-none absolute left-full top-0 z-50 hidden ml-3 group-hover:block group-focus-within:block">                            <div className="w-56 rounded-xl border p-4 text-center shadow-xl backdrop-blur bg-[var(--panel-bg)] text-[var(--panel-text)] border-gray-300">
                          <p className="text-xs font-semibold opacity-60">
                            {city.label}, {city.country}
                          </p>

                          <p className="mt-2 text-3xl font-bold">{times12[city.key]}</p>

                          <p className="text-xs mt-1 opacity-70">{dates[city.key]}</p>

                          <div className="mt-4 flex items-center justify-center gap-2 text-sm">
                            <span
                              className={`h-2 w-2 rounded-full ${officeHours[city.key]
                                ? "bg-[var(--color-brand)]"
                                : "bg-gray-500"
                                }`}
                            />

                            <span>
                              {officeHours[city.key]
                                ? t("timezoneBar.officeHoursNow", {
                                  defaultValue: "Office hours now",
                                })
                                : t("timezoneBar.officeHoursClosed", {
                                  defaultValue: "Outside office hours",
                                })}
                            </span>
                          </div>
                        </div>
                        </div>
                      </div>
                    ))}

                    {/*  Settings */}
                    <div className="border-t">
                      <button
                        role="menuitem"
                        onClick={() => setManageOpen(true)}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold hover:bg-gray-100"
                      >
                        <Settings01 className="w-4 h-4" />
                        {t("timezoneBar.settings", {
                          defaultValue: "Settings",
                        })}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/*  Drag reorder */}
                    {localCities.map((city, index) => (
                      <div
                        key={city.key}
                        draggable
                        onDragStart={onDragStart(index)}
                        onDragOver={onDragOver}
                        onDrop={onDrop(index)}
                        onTouchStart={(e) => {
                          setIsTouchDragging(true);
                          e.currentTarget.setAttribute("data-drag-index", String(index));
                        }}
                        onTouchMove={(e) => {
                          if (!isTouchDragging) return;

                          const touch = e.touches[0];
                          const target = document.elementFromPoint(touch.clientX, touch.clientY);

                          const dropEl = target?.closest("[data-drop-index]");
                          if (!dropEl) return;

                          const to = Number(dropEl.getAttribute("data-drop-index"));
                          const from = Number(e.currentTarget.getAttribute("data-drag-index"));

                          if (from === to) return;

                          const updated = [...localCities];
                          const [moved] = updated.splice(from, 1);
                          updated.splice(to, 0, moved);

                          setLocalCities(updated);
                          e.currentTarget.setAttribute("data-drag-index", String(to));
                        }}
                        onTouchEnd={() => setIsTouchDragging(false)}
                        data-drop-index={index}
                        className="flex justify-between px-2 py-1.5 text-xs border-b cursor-move bg-gray-50"
                      >
                        <span>{city.label}</span>

                        <Menu02 className="w-4 h-4 opacity-70" />
                      </div>
                    ))}

                    {/*  Actions */}
                    <div className="flex justify-end gap-2 p-2 border-t">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsTouchDragging(false);
                          setManageOpen(false);
                          setHiddenOpen(false);
                          setLocalCities(cities);
                        }}
                        className="px-2 py-1 text-xs bg-blue-600 text-white rounded"
                      >
                        {t("timezoneBar.cancel", {
                          defaultValue: "Cancel",
                        })}
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsTouchDragging(false);
                          handleSave();
                        }}
                        className="px-2 py-1 text-sm bg-blue-600 text-white rounded"
                      >
                        {t("timezoneBar.save", {
                          defaultValue: "Save",
                        })}
                      </button>
                    </div>
                  </>
                )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TimezoneBar;
